import { ShieldOff } from "lucide-react";

import {
  hasStaleAcknowledgment,
  isFinalQuestioningOff,
  useFinalQuestioningQuery,
  type FinalQuestioningStaleReason,
} from "@/api/finalQuestioning";
import { Notice } from "@/ui/Notice";

/**
 * The persistent "final safety questioning is off" warning (#853).
 *
 * ## WHY ONE COMPONENT FOR FOUR SURFACES
 *
 * Contract §8: "Disabled status remains prominent in Project header, Safety,
 * validation and preview, with a labelled warning icon and text; do not rely on
 * color or a skull alone." Four surfaces, one fact — so one component reading
 * one cache entry (`queryKeys.projectFinalQuestioning`). A warning assembled
 * separately per screen is a warning that eventually appears on three of them.
 *
 * The four differ only in the SECOND sentence, which says what the state means
 * where the clinician is standing. The first sentence is identical everywhere
 * on purpose: it is the fact, and a clinician moving between screens must not
 * have to re-read a paraphrase to check it is the same one.
 *
 * ## ICON *PLUS* TEXT, AND THE TEXT CARRIES THE MEANING
 *
 * The glyph is `aria-hidden`, which is not a weaker treatment than labelling
 * it — it is the correct one. `Notice` renders the icon beside a paragraph
 * that states the whole fact in words, so the warning survives a screen reader,
 * a monochrome display and a stylesheet that never loaded. Labelling the icon
 * too would announce the same thing twice. What §8 forbids is the opposite
 * arrangement: colour, or a glyph, doing the work alone.
 *
 * `role="status"` rather than `alert`: the state is a standing property of the
 * Project on every one of these screens, not something that has just been
 * refused. `Notice` keeps the region mounted and toggles `visible`, so a
 * clinician who disables final questioning hears the warning ARRIVE rather than
 * finding a region that was always there.
 *
 * ## WHAT ABSENCE MEANS, AND WHERE IT IS ALLOWED TO
 *
 * A missing warning must never be read as "final questioning is on". So an
 * unreadable state is rendered EXPLICITLY as unknown on the three surfaces a
 * decision is taken on — Safety, validation and preview. The Project header is
 * the one exception: it stands above every screen in the workspace, and a
 * transient read failure there would put a permanent unknown banner over
 * surfaces that have nothing to do with safety, while the three decision
 * surfaces already say it. The header's absence is therefore never
 * load-bearing — the decision surfaces are.
 */
export type FinalQuestioningSurface =
  | "header"
  | "safety"
  | "validation"
  | "preview";

/** The fact. Identical on every surface, deliberately. */
const OFF_HEADLINE = "Final safety questioning is off for this project.";

/**
 * What being off means WHERE THE CLINICIAN IS. Each sentence is about the next
 * action available on that screen, which is the only thing a fifth repetition
 * of the headline could not add.
 */
const OFF_DETAIL: Record<FinalQuestioningSurface, string> = {
  header:
    "The interview will not run its final safety questioning stage. Protected safety wording and the safety path documents are unchanged.",
  safety:
    "The interview will not run its final safety questioning stage. The safety paths below are unchanged and stay read-only; turning it back on is one action away.",
  validation:
    "Anything frozen from this draft carries that choice, and a real preview of it is refused unless the deployment target's own per-message safety detection is enabled and compatible.",
  preview:
    "A test version frozen from this draft carries that choice. Whether a real preview may run against a target is decided by the target's own runtime detection, not here.",
};

const STALE_HEADLINE =
  "Final safety questioning is off, and the confirmation recorded for it is out of date.";

const STALE_DETAIL: Record<FinalQuestioningStaleReason, string> = {
  draft_changed:
    "The project's draft has moved since the consequences were confirmed, so the confirmation no longer covers what would be frozen. Re-read the consequences on the Safety section and confirm again.",
  contract_revised:
    "This deployment has revised the consequences since they were confirmed, so the confirmation covers wording the server no longer states. Re-read them on the Safety section and confirm again.",
};

const UNREADABLE =
  "This project's final safety-questioning state could not be read, so this screen cannot say whether the interview runs its final safety questioning. Reload before freezing or previewing.";

export function FinalQuestioningWarning({
  projectId,
  surface,
  className,
}: {
  projectId: string;
  surface: FinalQuestioningSurface;
  className?: string;
}) {
  const query = useFinalQuestioningQuery(projectId);
  const state = query.data;

  // An unresolved read is UNKNOWN, never "on". Stated on the three decision
  // surfaces; see the module note for why the header is the one exception.
  // `isPending` is excluded deliberately: a query still in flight has not
  // failed, and a banner during every navigation would train a clinician to
  // ignore this region.
  const unreadable = state === undefined && query.isError;
  if (unreadable) {
    return surface === "header" ? null : (
      <Notice
        tone="warning"
        icon={<ShieldOff className="h-4 w-4" />}
        className={className ?? ""}
      >
        {UNREADABLE}
      </Notice>
    );
  }

  const off = isFinalQuestioningOff(state);
  const stale = off && hasStaleAcknowledgment(state);
  const staleReason = state?.acknowledgment?.staleReason ?? null;

  return (
    // MOUNTED WHATEVER THE STATE, visibility toggled: `Notice` renders an
    // `sr-only` live region when hidden, so a screen reader observes the
    // warning being ADDED at the moment the clinician confirms rather than
    // discovering a region that appeared with the page.
    <Notice
      tone="warning"
      visible={off}
      icon={<ShieldOff className="h-4 w-4" />}
      className={className ?? ""}
    >
      <span className="font-semibold">
        {stale ? STALE_HEADLINE : OFF_HEADLINE}
      </span>{" "}
      {stale && staleReason
        ? STALE_DETAIL[staleReason]
        : OFF_DETAIL[surface]}
    </Notice>
  );
}
